import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyOpenaisafetyMode({ brand, today, Copy }: ModeRendererProps) {
  // OpenAI Safety gradient: responsible AI green to trust blue
  const safetyGradient = (
    <defs>
      <linearGradient id="safetyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#10A37F" />
        <stop offset="50%" stopColor="#1E88E5" />
        <stop offset="100%" stopColor="#FF6B6B" />
      </linearGradient>
      <radialGradient id="guardrailGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#10A37F" stopOpacity={0.5} />
        <stop offset="100%" stopColor="#1E88E5" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Guardrail frames: safety boundaries
  const guardrailFrames: JSX.Element[] = [];
  for (let i = 0; i < 12; i++) {
    const size = 100 + i * 40;
    const x = 397 - size / 2;
    const y = 561.5 - size / 2;
    guardrailFrames.push(
      <rect
        key={`guardrail-${i}`}
        x={x}
        y={y}
        width={size}
        height={size}
        fill="none"
        stroke={i % 2 === 0 ? "#10A37F" : "#1E88E5"}
        strokeWidth={2}
        opacity={0.3}
        rx={8}
      />
    );
  }

  // Capability hulls: model boundaries
  const capabilityHulls: JSX.Element[] = [];
  const hullPoints = [
    [397, 241.5],
    [547, 391.5],
    [547, 731.5],
    [397, 881.5],
    [247, 731.5],
    [247, 391.5],
  ];
  for (let i = 0; i < 6; i++) {
    const scale = 0.3 + i * 0.12;
    const scaledPoints = hullPoints.map(([x, y]) => {
      const dx = x - 397;
      const dy = y - 561.5;
      return `${397 + dx * scale},${561.5 + dy * scale}`;
    });
    capabilityHulls.push(
      <polygon
        key={`hull-${i}`}
        points={scaledPoints.join(" ")}
        fill="none"
        stroke="url(#safetyGrad)"
        strokeWidth={1.5}
        opacity={0.3}
      />
    );
  }

  // Red-teaming nets: adversarial testing grid
  const redteamingNets: JSX.Element[] = [];
  for (let row = 0; row < 14; row++) {
    for (let col = 0; col < 10; col++) {
      const x = 100 + col * 60;
      const y = 120 + row * 65;
      const threat = Math.random() > 0.7;
      if (threat) {
        redteamingNets.push(
          <circle
            key={`redteam-${row}-${col}`}
            cx={x}
            cy={y}
            r={8}
            fill="#FF6B6B"
            opacity={0.4}
          />
        );
      } else {
        redteamingNets.push(
          <circle
            key={`safe-${row}-${col}`}
            cx={x}
            cy={y}
            r={5}
            fill="#10A37F"
            opacity={0.3}
          />
        );
      }
    }
  }

  // Alignment vectors: value alignment
  const alignmentVectors: JSX.Element[] = [];
  for (let i = 0; i < 16; i++) {
    const angle = (i * Math.PI * 2) / 16;
    const x1 = 397 + 80 * Math.cos(angle);
    const y1 = 561.5 + 80 * Math.sin(angle);
    const x2 = 397 + 200 * Math.cos(angle);
    const y2 = 561.5 + 200 * Math.sin(angle);
    alignmentVectors.push(
      <line
        key={`align-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="url(#safetyGrad)"
        strokeWidth={2}
        opacity={0.4}
        strokeDasharray="4,4"
      />
    );
  }

  // Central safety core
  const safetyCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={110} fill="url(#guardrailGlow)" />
      <circle cx={397} cy={561.5} r={75} fill="none" stroke="#10A37F" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={55} fill="none" stroke="#1E88E5" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={35} fill="none" stroke="#FF6B6B" strokeWidth={2} />
      {/* Shield symbol */}
      <path
        d="M 397 506.5 L 437 526.5 L 437 576.5 Q 437 596.5 397 611.5 Q 357 596.5 357 576.5 L 357 526.5 Z"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={3}
      />
    </g>
  );

  return (
    <g>
      {safetyGradient}
      {guardrailFrames}
      {capabilityHulls}
      {redteamingNets}
      {alignmentVectors}
      {safetyCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
